import React, { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { observer, inject } from 'mobx-react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductImage from '../products/ProductImage';
import Note2Icon from '../svg/Note2Icon';
import SaleInfo from './SaleInfo';
import { formatPrice, getPriceDetails, renderPriceCart, renderTypePriceCart, renderWeightPriceCart, renderVariationPriceCart, getPerUnit, getPerUnitDisplay } from '@/utils/misc';
import { calculateSavings, calculateTotalPrice } from '@/utils/bulkDiscounts';
import QuantityControls from '@/components/products/QuantityControls';

const MiniCartProduct = inject('wooCommerceStore', 'studioStore')(observer(({
  item,
  notification = false,
  wooCommerceStore,
  studioStore,
}) => {
  const [hovered, setHovered] = useState(false);  // State to track hover
  const [isRemoved, setIsRemoved] = useState(false);  // State to track removal
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [note, setNote] = useState(item.note || '');
  const tu = useTranslations('Units');
  const t = useTranslations('Cart');
  const weightUnit = wooCommerceStore.weightUnit || 'kg';
  const productType = item?.type ?? "";
  const productDesign = studioStore.cart.product;
  const secondaryTextColor = studioStore.settings.colors.text_secondary_color;
  const secondaryColor = studioStore.settings.colors.secondary_color;

  const priceDetailsData = {
    id: item.id,
    regular_price: item.regular_price,
    sale_price: item.sale_price,
    weight: item.weight,
    weightable: item.weightable,
    units_based: item.units_based,
    pack_based: item.pack_based,
    bulk_discount_rules: item.bulk_discount_rules,
    parent_id: item.parent_id,
    parent_name: item.parent_name,
    type: productType,
    price: item.price,
    weight_quantity_based: item.weight_quantity_based,
  };

  const priceDetails = getPriceDetails(priceDetailsData, weightUnit, tu, productType);

  // Memoize the product display type calculation
  const productDisplayType = useMemo(() => {
    return getPerUnit({
      perUnit: item?.per_unit,
      weightUnit,
      tu,
    });
  }, [item?.per_unit, weightUnit, tu]);

  const productDisplayTypeDisplay = useMemo(() => {
    return getPerUnitDisplay({
      perUnit: item?.per_unit_display,
      weightUnit,
      tu,
    });
  }, [item?.per_unit_display, weightUnit, tu]);

  let img = (item?.images?.[0]?.src) ? item.images[0] : {};

  let price = '';
  let displayName = '';

  if (productType === 'simple') {
    displayName = (
      <>
        <div className="leading-none mb-1" data-testid={`product-name-${item.id}`}>{item.name}</div>
        {item.weightable && item.weight > 0 && item.regular_price > 0 ? (
          <div className="text-xs">{priceDetails.displayWeight}</div>
        ) : null}
      </>
    );
    if (item.weightable && item.weight > 0 && item.regular_price > 0) {
      price = renderWeightPriceCart(priceDetails, productDisplayType);
    } else if (!item.weightable && (item.units_based || item.pack_based)) {
      price = renderTypePriceCart(priceDetails, productDisplayType);
    } else {
      price = renderPriceCart(priceDetails, productDisplayType);
    }
  } else if (productType === 'variable' || productType === 'variation') {
    img = (item?.image?.src) ? item.image : (item?.parent_images?.[0] ?? {});
    if (item.weightable && item.weight > 0 && item.regular_price > 0) {
      price = renderWeightPriceCart(priceDetails, productDisplayType);
    } else {
      price = renderVariationPriceCart(priceDetails, productDisplayType);
    }
    displayName = (
      <>
        <div className="leading-none mb-1" data-testid={`product-name-${item.id}`}>{item.parent_name}</div>
        <div className="text-xs">
          {productDisplayType}
          {item.weightable && item.weight > 0 && item.regular_price > 0 ? (
            <div className="text-xs">{priceDetails.displayWeight}</div>
          ) : null}
        </div>
      </>
    );
  }

  const handleAddToCart = () => {
    wooCommerceStore.addToCart(item, 1);
  };

  const handleClearFromCart = () => {
    setIsRemoved(true);
    setTimeout(() => {
      wooCommerceStore.removeFromCart(item.id);
    }, 500);
  };

  const handleRemoveFromCart = () => {
    if (item.quantity > 0) {
      wooCommerceStore.updateCartItemQuantity(item.id, item.quantity - 1);
    }
  };

  const handleUpdateQuantity = (newQuantity) => {
    wooCommerceStore.updateCartItemQuantity(item.id, newQuantity);
  };

  const handleSaveNote = () => {
    wooCommerceStore.updateCartItemNote(item.id, note);
    setShowNoteInput(false);
  };

  // Calculate the saved amount using the utility function
  const savedAmount = calculateSavings(item);

  // Check if there's a regular sale
  const hasRegularSale = item.sale_price && item.regular_price &&
    parseFloat(item.regular_price) > parseFloat(item.sale_price);

  return (
    <AnimatePresence>
      {!isRemoved && (
        <motion.div
          key={item.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{backgroundColor: (productDesign?.background_color ? productDesign?.background_color : 'white')}}
          className={`${productDesign?.border_radius} relative flex flex-wrap items-start py-2 px-2 ${!notification && 'mb-3'}`}
          onMouseEnter={() => setHovered(true)}  // Set hover state
          onMouseLeave={() => setHovered(false)} // Remove hover state
        >
          <div className="relative">
            <ProductImage
              img={img}
              productName={item?.name}
              width={64}
              height={64}
              className="h-16 w-16 object-contain rounded-lg mr-2 rtl:mr-0 rtl:ml-2"
            />

            <AnimatePresence>
              {hovered && (
                <motion.button
                  key="remove-button"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-0 right-0 bg-gray-400 text-white rounded-full h-5 w-5 flex items-center justify-center text-sm"
                  onClick={handleClearFromCart}
                  aria-label="Remove from cart"
                >
                  &times;
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          <div className="flex-1 p-1 text-sm" style={{color: secondaryTextColor}}>
            {displayName}
            {!notification && (
              <div className="flex items-center mt-1 text-xs cursor-pointer" onClick={() => setShowNoteInput(!showNoteInput)}>
                <Note2Icon className="w-4 h-4 me-1" />
                {note ? note : t('add_note')}
              </div>
            )}
            {showNoteInput &&  (
              <div className="mt-1">
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full text-xs p-1 border rounded"
                  placeholder={t('enter_note')}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveNote()}
                  onBlur={handleSaveNote}
                  autoFocus
                />
              </div>
            )}
          </div>
          <div className="quantity w-[25%] p-1" data-testid={`quantity-controls-${item.id}`}>
            <QuantityControls
              handleRemoveFromCart={handleRemoveFromCart}
              handleAddToCart={handleAddToCart}
              handleUpdateQuantity={handleUpdateQuantity}
              quantity={item.quantity}
              productDisplayType={productDisplayTypeDisplay}
              productWeightQuantity={item.weight_quantity_based}
              compact
              productId={item.id}
              productStockStatus={item.stock_status}
              variant="cart"
            />
          </div>
          <div className="price p-1 w-[24%] text-right rtl:text-left" style={{color: secondaryTextColor}}>
            <p className={`font-semibold ${(savedAmount > 0 || hasRegularSale) && 'text-secondary-studio'}`}>{formatPrice(calculateTotalPrice(item))}</p>
            <div className="text-xs">{price}</div>
          </div>
          {/* Display sale info for both regular and bulk sales */}
          <SaleInfo
            item={item}
            secondaryColor={secondaryColor}
            secondaryTextColor={secondaryTextColor}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}));

export default MiniCartProduct;